import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { type AuthUser, getUserModules, UserModuleIDs } from '@enroller/utils';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHeaderContext } from '../contexts/HeaderContext';
import { ErrorView } from '../layout/ErrorView';
import { LoadingView } from '../layout/LoadingView';
import { getErrorMessage } from '../utils/getErrorMessage';
import { useNotificationMessages } from '../utils/useNotificationMessages';
import { useStatelessGet } from '../utils/useStatelessGet';
import { Addon, ProgramCurrency } from './Addons';
import {
  CourseReservation,
  courseReservationDataQuery,
  courseReservationIdsQuery,
  courseReservationProgramDetailsQuery,
  graphQlToForm,
} from './CourseReservation';
import { CourseReservationForm } from './CourseReservationForm';
import { Alert, Stack, Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { format } from 'date-fns';

const FormSubmittedView = ({ submissionDate }: { submissionDate: string }) => {
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <Stack direction="column" spacing={3} sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Alert severity="info" variant="outlined">
        <Stack spacing={2} padding={2}>
          <Typography variant="h6">
            <Trans>Application Already Submitted</Trans>
          </Typography>
          <Typography variant="body2">
            <Trans>Your course reservation application was submitted on {formatDate(submissionDate)}. You can no longer edit the form details.</Trans>
          </Typography>
          <Button component={Link} to="/" variant="contained" sx={{ alignSelf: 'flex-start' }}>
            <Trans>Return to Dashboard</Trans>
          </Button>
        </Stack>
      </Alert>
    </Stack>
  );
};

export const CourseReservationView = () => {
  const { t } = useLingui();
  const { user } = useAuth0<AuthUser>();
  const [formValues, setFormValues] = useState<CourseReservation | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [ownerId, setOwnerId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [guardian1Id, setGuardian1Id] = useState('');
  const [guardian2Id, setGuardian2Id] = useState('');
  const [programName, setProgramName] = useState('');
  const [programCost, setProgramCost] = useState(0);
  const [programCurrency, setProgramCurrency] = useState<ProgramCurrency>('USD');
  const [existingAddons, setExistingAddons] = useState<{ selectionId: string; addonId: string }[] | undefined>(undefined);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [error, setError] = useState(false);
  const [errorAddons, setErrorAddons] = useState(false);
  const [submissionDate, setSubmissionDate] = useState<string | null>(null);
  const [dataFetched, setDataFetched] = useState(false);
  const userModules = useMemo<UserModuleIDs | undefined>(() => user && getUserModules(user), [user]);
  const { refetch: fetchIds } = useQuery(courseReservationIdsQuery, { skip: true });
  const { refetch: fetchData } = useQuery(courseReservationDataQuery, { skip: true });
  const { refetch: fetchProgram } = useQuery(courseReservationProgramDetailsQuery, { skip: true });
  const fetchAddons = useStatelessGet<Addon[]>(`/deals/${userModules?.dealId}/sub-products`);
  const { showErrorMessage } = useNotificationMessages();
  const { setHeaderTitle, setIsFormSubmitted } = useHeaderContext();
  const AddonsEnabled = false;

  useEffect(() => {
    return () => {
      setIsFormSubmitted(false);
    };
  }, [setIsFormSubmitted]);

  const getAddons = useCallback(async () => {
    try {
      setLoadingAddons(true);
      const response = await fetchAddons();
      // TODO: this is to account for inconsistent api payload
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      setAddons((response as any).data ? (response as any).data : response);
      setErrorAddons(false);
    } catch (error) {
      showErrorMessage(getErrorMessage(error));
      setErrorAddons(true);
    } finally {
      setLoadingAddons(false);
    }
  }, [fetchAddons, showErrorMessage]);

  const getCourseReservation = useCallback(
    async (dealId: string) => {
      try {
        setLoading(true);
        const { data: ids } = await fetchIds({ dealId });

        const submittedDate = ids?.Records?.Deals?._data[0]?.Date_of_Contact_Details_Form_Response?.value;
        setSubmissionDate(submittedDate || null);
        setIsFormSubmitted(!!submittedDate);

        if (submittedDate) {
          setDataFetched(true);
          setLoading(false);
          return;
        }

        const owner = ids?.Records?.Deals?._data[0]?.Owner;
        const student = ids?.Records?.Deals?._data[0]?.Contact_Name;
        const guardian1 = ids?.Records?.Deals?._data[0]?.Guardian_primary;
        const guardian2 = ids?.Records?.Deals?._data[0]?.Guardian_second;
        const addonSelections = ids?.Records?.Deals?._data[0]?.Addons_Selection__r?._data || [];
        setOwnerId(owner?.id?.value || '');
        setStudentId(student?.id?.value || '');
        setGuardian1Id(guardian1?.id?.value || '');
        setGuardian2Id(guardian2?.id?.value || '');
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const addons = addonSelections.map((selection: any) => {
          return {
            selectionId: selection.id.value,
            addonId: selection.Addon.id.value,
          };
        });
        setExistingAddons(addons);
        const { data: courseReservationData } = await fetchData({
          dealId,
          studentId: student?.id?.value || '',
          guardian1Id: guardian1?.id?.value || '',
          guardian1Exists: Boolean(guardian1?.id?.value),
          guardian2Id: guardian2?.id?.value || '',
          guardian2Exists: Boolean(guardian2?.id?.value),
        });
        const customerData = JSON.parse(ids?.Records?.Deals?._data[0]?.Last_Addon_Data?.value);
        const { data: programData } = await fetchProgram({
          productId: customerData.current.program,
          customerCode: customerData.current.Customer_Group_code,
        });
        const productName = programData?.Records?.Products?._data[0]?.Product_Name?.value || '';
        const description = programData?.Records?.Products?._data[0]?.Description?.value || '';
        setProgramName(description.trim() !== '' ? description : productName);
        setProgramCost(ids?.Records?.Deals?._data[0]?.Price?.value || 0);
        setProgramCurrency(programData?.Records?.Products?._data[0]?.Pricing_Information__r?._data[0]?.Pricing_Currency?.value || 'USD');
        setFormValues(graphQlToForm(courseReservationData, addonSelections));
        setDataFetched(true);
        setError(false);
      } catch (error) {
        showErrorMessage(getErrorMessage(error));
        setError(true);
        setDataFetched(true);
      } finally {
        setLoading(false);
      }
    },
    [fetchIds, fetchData, fetchProgram, showErrorMessage, setIsFormSubmitted],
  );

  useEffect(() => {
    if (userModules?.dealId && !dataFetched && !loading && !error) {
      getCourseReservation(userModules.dealId);
    }
  }, [getCourseReservation, userModules.dealId, loading, error, dataFetched]);

  useEffect(() => {
    if (AddonsEnabled && userModules?.dealId && !addons.length && !loadingAddons && !errorAddons) {
      getAddons();
    }
  }, [AddonsEnabled, addons.length, errorAddons, getAddons, loadingAddons, userModules?.dealId]);

  useEffect(() => {
    setHeaderTitle({ title: t`Course Reservation`, subtitle: t`Student Details` });
    setIsFormSubmitted(true);
  }, [setHeaderTitle, t, setIsFormSubmitted]);

  if (loading || loadingAddons) return <LoadingView />;

  if (error || errorAddons) return <ErrorView message="An error occurred" />;

  if (submissionDate) {
    return <FormSubmittedView submissionDate={submissionDate} />;
  }

  if (!formValues) return <ErrorView message="Form data not available" />;

  return (
    <CourseReservationForm
      {...{
        formValues,
        dealId: userModules.dealId,
        ownerId,
        studentId,
        guardian1Id,
        guardian2Id,
        addons,
        programName,
        programCost,
        programCurrency,
        existingAddons: existingAddons!,
        onFormSuccess: () => getCourseReservation(userModules.dealId),
      }}
    />
  );
};
